import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { entityService } from "../services/entityService";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Paper,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { Delete, Edit } from "@mui/icons-material";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import EntityModal from "../components/modals/EntityModal";
import { EntityData } from "../interfaces/EntityData";
import ConfirmModal from "../components/modals/ConfirmModal";
import { FaqButton } from "../components/FaqButton";
import StandardPagination from "../components/Pagination/StandardPagination";
import AddButton from "../components/AddButton";
import ResultCount from "../components/ResultCount";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { colors, inputs } from "../theme/designSystem";
import { Entity } from "../interfaces/adminEntities";
import { EntityRequest, EntityResponse } from "../types/entity";
import { maskDocument } from "../utils/document";
import { BRAZIL_DDI } from "../utils/phone";

/** Minusculas e sem acento: quem busca "sao paulo" espera achar "São Paulo".
 *  Aceita nulo: campo vazio na base não pode derrubar a busca. */
const normalize = (valor: string | null | undefined): string =>
  (valor || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const AdminEntities: React.FC<{}> = () => {
  const queryClient = useQueryClient();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();
  const { user } = useAuth();

  // Estados dos filtros e paginação
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<"todas" | "ativas" | "inativas">("todas");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortField, setSortField] = useState<
    "fantasyName" | "cnpjCpf" | "socialName"
  >("fantasyName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [hasFilterChanges, setHasFilterChanges] = useState(false);

  // Estados dos modais
  const [isEntityModalOpen, setIsEntityModalOpen] = useState(false);
  const [entityModalMode, setEntityModalMode] = useState<"create" | "edit">(
    "create"
  );
  const [entityToEdit, setEntityToEdit] = useState<Entity | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState<Entity | null>(null);
  const [isInactivateModalOpen, setIsInactivateModalOpen] = useState(false);
  const [entityToInactivate, setEntityToInactivate] = useState<Entity | null>(
    null
  );

  // Funções de manipulação de eventos do Header
  const handleNotificationClick = () => {
    console.log("Notificações clicadas");
  };

  const handleUserClick = () => {
    console.log("Menu do usuário clicado");
  };

  // Valores iniciais dos filtros
  const initialFilters = useMemo(
    () => ({
      searchTerm: "",
      status: "todas" as const,
      sortField: "fantasyName" as const,
      sortOrder: "asc" as const,
    }),
    []
  );

  // Buscar entidades do backend usando react-query
  const {
    data: entitiesResponse,
    isLoading,
    isError: isEntitiesError,
    refetch: refetchEntities,
  } = useQuery<EntityResponse[]>({
    queryKey: ["entities"],
    queryFn: entityService.getAllEntities,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  // Converter a resposta da API para o formato usado no componente
  const entities = useMemo(() => {
    if (!entitiesResponse) return [];
    return entitiesResponse.map((item: EntityResponse) => {
      // Log para debug dos dados recebidos da API
      console.log("Dados recebidos da API:", {
        item,
        endereco: {
          cep: item.addressZipcode,
          addressCity: item.addressCity,
          street: item.addressStreet,
          number: item.addressNumber,
          complement: item.addressComplement,
          neighborhood: item.addressNeighborhood,
          state: item.addressState,
        },
      });

      const entity = {
        id: item.id,
        fantasyName: item.entityNickName,
        cnpjCpf: item.document,
        socialName: item.companyName,
        entityType: (item.personType || "")
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .toLowerCase()
          .includes("fisica")
          ? ("fisica" as const)
          : ("juridica" as const),
        inscricaoEstadual: item.inscricaoEstadual,
        inscricaoMunicipal: item.inscricaoMunicipal,
        ddd: item.phoneCodeArea,
        phone: item.phone,
        phoneDdi: item.phoneDdi ?? undefined,
        email: item.email,

        // Campos de endereço - mantendo os valores exatamente como vêm da API
        addressZipcode: item.addressZipcode,
        addressStreet: item.addressStreet,
        addressNumber: item.addressNumber,
        addressComplement: item.addressComplement,
        addressNeighborhood: item.addressNeighborhood,
        addressCity: item.addressCity,
        addressState: item.addressState,

        // Campos de horário
        workingHours:
          item.initialWorkHour && item.finalWorkHour
            ? `${item.initialWorkHour} - ${item.finalWorkHour}`
            : "08:00 - 18:00",
        startTime: item.initialWorkHour || "08:00",
        endTime: item.finalWorkHour || "18:00",

        isActive: item.isActive,
        hasRegistrations: item.hasRegistrations,
        timeZone: item.timeZone,
        financialCutoffDate: item.financialCutoffDate ?? "",
      };

      console.log("Entidade mapeada:", entity);
      return entity;
    });
  }, [entitiesResponse]);

  // Filtrar e ordenar entidades
  const filteredAndSortedEntities = useMemo(() => {
    const busca = normalize(searchTerm);
    const digitos = searchTerm.replace(/\D/g, "");

    return entities
      .filter((entity) => {
        if (status !== "todas" && entity.isActive !== (status === "ativas")) {
          return false;
        }
        if (searchTerm === "") {
          return true;
        }
        // O CNPJ e guardado com pontuacao: sem comparar so os digitos, quem digita
        // "44594155" nao encontra "44.594.155/0001-79".
        const achouDocumento = digitos.length > 0
          && (entity.cnpjCpf || "").replace(/\D/g, "").includes(digitos);

        return achouDocumento
          || normalize(entity.fantasyName).includes(busca)
          || normalize(entity.socialName).includes(busca)
          || normalize(entity.cnpjCpf).includes(busca);
      })
      .sort((a, b) => {
        let compareValue = 0;
        if (sortField === "fantasyName") {
          compareValue = a.fantasyName.localeCompare(b.fantasyName);
        } else if (sortField === "cnpjCpf") {
          compareValue = a.cnpjCpf.localeCompare(b.cnpjCpf);
        } else if (sortField === "socialName") {
          compareValue = a.socialName.localeCompare(b.socialName);
        }
        return sortOrder === "asc" ? compareValue : -compareValue;
      });
  }, [entities, searchTerm, status, sortField, sortOrder]);

  // Calcular paginação
  const totalItems = filteredAndSortedEntities.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEntities = filteredAndSortedEntities.slice(
    startIndex,
    endIndex
  );

  // Handler para salvar/editar entidade
  const handleSaveEntity = async (data: Entity) => {
    try {
      // As faixas de atendimento têm modal próprio na lista; a entidade preserva as
      // colunas legadas (Bubble-master) do próprio form, sem derivá-las aqui.
      const initialWorkHour = data.startTime || "";
      const finalWorkHour = data.endTime || "";

      const entityRequest: EntityRequest = {
        active: true,
        id: data.id || "",
        addressCity: data.addressCity || "",
        addressComplement: data.addressComplement || "",
        addressNeighborhood: data.addressNeighborhood || "",
        addressNumber: data.addressNumber || "",
        addressState: data.addressState || "",
        addressStreet: data.addressStreet || "",
        addressZipcode: data.addressZipcode || "",
        companyName: data.socialName || "",
        // A tela não edita a entidade padrão: na edição mantém o que está gravado
        defaultEntity: entityModalMode === "edit" && entityToEdit
          ? entitiesResponse?.find((e) => e.id === entityToEdit.id)?.defaultEntity ?? false
          : false,
        document: (data.cnpjCpf || "").replace(/\D/g, ""),
        email: data.email || "",
        entityNickName: data.fantasyName || "",
        finalWorkHour,
        initialWorkHour,
        inscricaoEstadual: data.inscricaoEstadual || "",
        inscricaoMunicipal: data.inscricaoMunicipal || "",
        personType: data.entityType === "fisica" ? "Pessoa Física" : "Pessoa Jurídica",
        phone: data.phone || "",
        // Fora do Brasil não há DDD: o campo vai vazio para limpar o que estava gravado.
        phoneCodeArea: data.ddd || "",
        phoneDdi: data.phoneDdi || BRAZIL_DDI,
        whatsappNumber: 0,
        slug: data.fantasyName?.toLowerCase().replace(/ /g, "-") || "entity",
        timeZone: data.timeZone || "America/Sao_Paulo",
      };
      if (entityModalMode === "edit") {
        entityRequest.financialCutoffDate = data.financialCutoffDate ?? "";
      }

      if (entityModalMode === "create") {
        await entityService.createEntity(entityRequest);
        showToast("Entidade cadastrada com sucesso", "success");
      } else if (entityToEdit) {
        entityRequest.id = entityToEdit.id;
        await entityService.updateEntity(entityToEdit.id, entityRequest);
        showToast("Entidade atualizada com sucesso", "success");
      }

      await queryClient.invalidateQueries({ queryKey: ["entities"] });
      setIsEntityModalOpen(false);
      setEntityToEdit(null);
    } catch (error) {
      console.error("Erro ao salvar entidade:", error);
      showToast(
        "Erro ao salvar entidade. Por favor, tente novamente.",
        "error"
      );
    }
  };
  const handleRevalidateLogin = () => {
    console.log("Revalidando login...");
  };

  const handleLogoClick = () => {
    goToDashboard();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setTimeout(scrollToTop, 100);
  };

  const handleSort = (field: "fantasyName" | "cnpjCpf" | "socialName") => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  // Reset página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, status, sortOrder]);

  // Verificar se há mudanças nos filtros
  useEffect(() => {
    const hasChanges =
      searchTerm !== initialFilters.searchTerm ||
      status !== initialFilters.status ||
      sortField !== initialFilters.sortField ||
      sortOrder !== initialFilters.sortOrder;

    setHasFilterChanges(hasChanges);
  }, [searchTerm, status, sortField, sortOrder, initialFilters]);

  const clearFilters = () => {
    setSearchTerm(initialFilters.searchTerm);
    setStatus(initialFilters.status);
    setSortField(initialFilters.sortField);
    setSortOrder(initialFilters.sortOrder);
    setCurrentPage(1);
    setHasFilterChanges(false);
  };

  const handleAddEntity = () => {
    setEntityModalMode("create");
    setEntityToEdit(null);
    setIsEntityModalOpen(true);
  };

  const handleEntityAction = (action: string, entityId: string) => {
    const entity = entities.find((e: Entity) => e.id === entityId);
    if (!entity) return;

    switch (action) {
      case "view":
        showToast(`Visualizando entidade: ${entity.fantasyName}`, "info");
        break;
      case "edit":
        setEntityModalMode("edit");
        setEntityToEdit(entity);
        setIsEntityModalOpen(true);
        break;
      case "delete":
        setEntityToDelete(entity);
        setIsDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (entityToDelete) {
      try {
        await entityService.deleteEntity(entityToDelete.id);
        showToast(
          `Entidade ${entityToDelete.fantasyName} removida com sucesso`,
          "success"
        );
        // Atualizar a lista de entidades
        await queryClient.invalidateQueries({ queryKey: ["entities"] });
        setIsDeleteModalOpen(false);
        setEntityToDelete(null);
      } catch (error) {
        console.error("Erro ao remover entidade:", error);
        showToast(
          "Erro ao remover entidade. Por favor, tente novamente.",
          "error"
        );
      }
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setEntityToDelete(null);
  };

  // Status da entidade: ativar e imediato; inativar exige confirmacao.
  // Update otimista: reflete o novo status na hora (o banco remoto tem latencia) e reverte se a API falhar.
  const applyEntityActive = async (entity: Entity, active: boolean) => {
    const patchCache = (value: boolean) =>
      queryClient.setQueryData<EntityResponse[]>(["entities"], (old) =>
        old?.map((e) =>
          e.id === entity.id ? { ...e, isActive: value } : e
        )
      );

    patchCache(active);

    try {
      await entityService.setEntityActive(entity.id, active);
      showToast(
        `Entidade ${entity.fantasyName} ${
          active ? "ativada" : "inativada"
        } com sucesso`,
        "success"
      );
      await queryClient.invalidateQueries({ queryKey: ["entities"] });
    } catch (error) {
      patchCache(!active);
      console.error("Erro ao alterar status da entidade:", error);
      showToast(
        "Erro ao alterar o status da entidade. Tente novamente.",
        "error"
      );
    }
  };

  const handleToggleActive = (entity: Entity) => {
    if (entity.isActive) {
      setEntityToInactivate(entity);
      setIsInactivateModalOpen(true);
    } else {
      applyEntityActive(entity, true);
    }
  };

  const handleConfirmInactivate = async () => {
    if (entityToInactivate) {
      await applyEntityActive(entityToInactivate, false);
      setIsInactivateModalOpen(false);
      setEntityToInactivate(null);
    }
  };

  const handleCloseInactivateModal = () => {
    setIsInactivateModalOpen(false);
    setEntityToInactivate(null);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (isEntitiesError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography color="error">
          Erro ao carregar entidades. Tente novamente.
        </Typography>
        <Button
          onClick={() => refetchEntities()}
          variant="contained"
          sx={{ ml: 2 }}
        >
          Tentar novamente
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={5}
        onRevalidateLogin={handleRevalidateLogin}
        onNotificationClick={handleNotificationClick}
        onUserClick={handleUserClick}
        onLogoClick={handleLogoClick}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          {/* Título da Página */}
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box className="c4u-page-header__texts">
              <Typography variant="h4" className="c4u-page-header__title">
                Entidades
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Clínicas e unidades cadastradas no sistema.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          {/* Filtros, Paginação e Lista */}
          <Paper elevation={0} className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  label="Busca por palavra"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: inputs.default.height,
                      "& fieldset": {
                        borderColor: colors.border,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.border,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.primary,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: inputs.default.labelFontSize,
                      color: colors.textSecondary,
                      backgroundColor: colors.white,
                      padding: inputs.default.labelPadding,
                      "&.Mui-focused": {
                        color: colors.primary,
                      },
                    },
                  }}
                />

                <TextField
                  select
                  label="Situação"
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value as "todas" | "ativas" | "inativas")
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": { height: inputs.default.height },
                  }}
                >
                  <MenuItem value="todas">Todas</MenuItem>
                  <MenuItem value="ativas">Ativas</MenuItem>
                  <MenuItem value="inativas">Inativas</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton
                  onClick={clearFilters}
                  disabled={!hasFilterChanges}
                />
                <AddButton
                  onClick={handleAddEntity}
                  title="Adicionar nova entidade"
                />
              </div>
            </div>

            <ResultCount
              shown={filteredAndSortedEntities.length}
              total={entities.length}
            />

            {/* Lista de Entidades */}
            <Box className="admin-plans-list-container" sx={{ mt: 2, background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
              <Box className="admin-plans-table admin-entities-table">
                <Box className="c4u-table__header">
                  <Box
                    className="admin-plans-header-cell"
                    sx={{
                      flex: "0 0 250px",
                      textAlign: "left",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("fantasyName")}
                    title="Ordenar por nome"
                  >
                    Nome{" "}
                    {sortField === "fantasyName"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}
                  </Box>
                  <Box
                    className="admin-plans-header-cell c4u-col--xl"
                    sx={{
                      flex: "0 0 180px",
                      textAlign: "left",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("cnpjCpf")}
                    title="Ordenar por CNPJ/CPF"
                  >
                    CNPJ/CPF{" "}
                    {sortField === "cnpjCpf"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}
                  </Box>
                  <Box
                    className="admin-plans-header-cell c4u-col--lg"
                    sx={{
                      flex: "1 1 auto",
                      textAlign: "left",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("socialName")}
                    title="Ordenar por razão social"
                  >
                    Razão Social{" "}
                    {sortField === "socialName"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}
                  </Box>
                  <Box
                    className="admin-plans-header-cell"
                    sx={{ flex: "0 0 110px", textAlign: "left" }}
                  >
                    Status
                  </Box>
                  <Box
                    className="admin-plans-header-cell"
                    sx={{ flex: "0 0 100px", justifyContent: "flex-end" }}
                  >
                    Ações
                  </Box>
                </Box>

                <Box className="admin-plans-table-body">
                  {paginatedEntities.map((entity: Entity) => (
                    <Box
                      key={entity.id}
                      className="c4u-table__row"
                      sx={{
                        display: "flex",
                        gridTemplateColumns: "unset",
                      }}
                    >
                      <Box
                        className="admin-plans-cell"
                        sx={{ flex: "0 0 250px", textAlign: "left" }}
                        data-label="Nome"
                      >
                        {entity.fantasyName}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--xl"
                        sx={{ flex: "0 0 180px", textAlign: "left" }}
                        data-label="CNPJ/CPF"
                      >
                        {maskDocument(entity.cnpjCpf, entity.entityType)}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--lg"
                        sx={{
                          flex: "1 1 auto",
                          textAlign: "left",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                        data-label="Razão Social"
                      >
                        {entity.socialName}
                      </Box>
                      <Box
                        className="admin-plans-cell"
                        sx={{
                          flex: "0 0 110px",
                          textAlign: "left",
                          justifyContent: "flex-start",
                        }}
                        data-label="Status"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Chip
                          label={entity.isActive ? "Ativo" : "Inativo"}
                          size="small"
                          clickable
                          title={
                            entity.isActive
                              ? "Clique para inativar"
                              : "Clique para ativar"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleActive(entity);
                          }}
                          sx={{
                            cursor: "pointer",
                            fontWeight: 600,
                            color: "white",
                            backgroundColor: entity.isActive
                              ? "#28a745"
                              : "#adb5bd",
                            "&:hover": {
                              backgroundColor: entity.isActive
                                ? "#218838"
                                : "#9aa0a6",
                            },
                          }}
                        />
                      </Box>
                      <Box
                        className="admin-plans-cell admin-plans-actions"
                        sx={{ flex: "0 0 100px", textAlign: "right" }}
                        data-label="Ações"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEntityAction("edit", entity.id);
                            }}
                            title="Editar entidade"
                            sx={{
                              backgroundColor: "#03B4C6",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#029AAB",
                              },
                            }}
                          >
                            <Edit sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <Tooltip
                            title={
                              entity.hasRegistrations
                                ? "Não é possível excluir: possui vínculos de usuário/profissional"
                                : "Excluir entidade"
                            }
                          >
                            <span>
                              <IconButton
                                className="c4u-row-action"
                                size="small"
                                disabled={!!entity.hasRegistrations}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEntityAction("delete", entity.id);
                                }}
                                sx={{
                                  backgroundColor: "#dc3545",
                                  color: "white",
                                  "&:hover": {
                                    backgroundColor: "#c82333",
                                  },
                                  "&.Mui-disabled": {
                                    backgroundColor: "#e9a9af",
                                    color: "white",
                                  },
                                }}
                              >
                                <Delete sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Paginação Inferior */}
            <Box sx={{ mt: 2 }}>
              <StandardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filteredAndSortedEntities.length}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  setTimeout(scrollToTop, 100);
                }}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </Box>
          </Paper>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="Excluir Entidade"
        message={
          entityToDelete
            ? `Tem certeza que deseja excluir a entidade "${entityToDelete.fantasyName}" (${maskDocument(entityToDelete.cnpjCpf, entityToDelete.entityType)})?`
            : "Tem certeza que deseja excluir esta entidade?"
        }
        warningMessage="Esta ação não poderá ser desfeita."
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteModal}
        type="danger"
      />

      {/* Modal de Confirmação de Inativação */}
      <ConfirmModal
        isOpen={isInactivateModalOpen}
        title="Inativar Entidade"
        message={
          entityToInactivate
            ? `Tem certeza que deseja inativar a entidade "${entityToInactivate.fantasyName}"?`
            : "Tem certeza que deseja inativar esta entidade?"
        }
        warningMessage="A entidade ficará indisponível até ser reativada."
        confirmButtonText="Inativar"
        cancelButtonText="Cancelar"
        onConfirm={handleConfirmInactivate}
        onCancel={handleCloseInactivateModal}
        type="danger"
      />

      <EntityModal
        isOpen={isEntityModalOpen}
        onClose={() => setIsEntityModalOpen(false)}
        onSave={handleSaveEntity}
        mode={entityModalMode}
        initialData={
          entityToEdit
            ? {
                id: entityToEdit.id,
                fantasyName: entityToEdit.fantasyName,
                cnpjCpf: entityToEdit.cnpjCpf,
                socialName: entityToEdit.socialName,
                entityType: entityToEdit.entityType || "juridica",
                inscricaoEstadual: entityToEdit.inscricaoEstadual,
                inscricaoMunicipal: entityToEdit.inscricaoMunicipal,

                // Campos de contato
                ddd: entityToEdit.ddd,
                phone: entityToEdit.phone,
                phoneDdi: entityToEdit.phoneDdi,
                email: entityToEdit.email,

                // Campos de endereço - SEM valores padrão para preservar os valores reais
                addressZipcode: entityToEdit.addressZipcode,
                addressStreet: entityToEdit.addressStreet,
                addressNumber: entityToEdit.addressNumber,
                addressComplement: entityToEdit.addressComplement,
                addressNeighborhood: entityToEdit.addressNeighborhood,
                addressCity: entityToEdit.addressCity,
                addressState: entityToEdit.addressState,

                // Campos de horário
                workingHours: entityToEdit.workingHours || "08:00 - 18:00",
                startTime:
                  entityToEdit.workingHours?.split(" - ")[0] || "08:00",
                endTime: entityToEdit.workingHours?.split(" - ")[1] || "18:00",
                timeZone: entityToEdit.timeZone,
                financialCutoffDate: entityToEdit.financialCutoffDate,
              }
            : undefined
        }
      />
    </Box>
  );
};

export default AdminEntities;
